import { ReactNode, useId } from "react";
import { Box, Skeleton, Switch, SwitchProps, Tooltip, Typography, TypographyProps } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import styles from "./index.module.css";

// None = no text, left = text appears to the left, dual = label off to the left of switch, label on to the right of switch
type CoreSwitchLabelType = "none" | "left" | "dual";

export type CoreSwitchProps = Omit<SwitchProps, "checked"> & {
	width?: string;
	height?: string;
	checked: boolean;
	/**Text to show when toggle is on */
	labelOnText?: string;
	/**Text to show when toggle is off */
	labelOffText?: string;
	/**None = no text, left = text appears to the left, dual = label off to the left of switch, label on to the right of switch. Default: `none` */
	labelTextType?: CoreSwitchLabelType;
	disabledTooltip?: ReactNode;
	ariaLabelledBy?: string;
	slotProps?: {
		switchLabel?: Omit<TypographyProps, "children" | "id">;
	};
};

export function CoreSwitch({
	width = "50px",
	height = "27px",
	checked,
	labelOnText = i18nPackage.t("coreComponents.coreSwitch.enabled"),
	labelOffText = i18nPackage.t("coreComponents.coreSwitch.disabled"),
	labelTextType = "none",
	disabled,
	disabledTooltip,
	sx,
	ariaLabelledBy,
	slotProps,
	...props
}: CoreSwitchProps) {
	const disabledTooltipId = useId();
	const leftLabelId = useId();
	const rightLabelId = useId();

	const fallbackId = checked && labelTextType === "dual" ? rightLabelId : leftLabelId;
	const ariaLabelledByFinal = ariaLabelledBy ?? props.inputProps?.["aria-labelledby"] ?? fallbackId;

	let switchElement = (
		<Switch
			{...props}
			inputProps={{
				...props.inputProps,
				"aria-labelledby": ariaLabelledByFinal,
			}}
			disabled={disabled}
			sx={{
				width,
				height,
				"&.Mui-focusVisible": {
					borderRadius: `calc(${height} - 9px)`,
				},
				"& .MuiSwitch-switchBase.Mui-checked": {
					transform: `translateX(calc(${width} - ${height}))`,
				},
				"& .MuiSwitch-thumb": {
					width: `calc(${height} - 6px)`,
					height: `calc(${height} - 6px)`,
					borderRadius: `calc(${height} - 9px)`,
				},
				...sx,
			}}
			checked={checked}
		/>
	);

	if (disabled && disabledTooltip) {
		switchElement = (
			<Tooltip id={disabledTooltipId} title={disabledTooltip}>
				<Box
					aria-label={undefined}
					aria-describedby={disabledTooltipId}
					tabIndex={0}
					sx={{
						borderRadius: `calc(${height} - 9px)`,
						"&:focus-visible": {
							outline: "2px solid var(--focus-outline-color-dark)",
							outlineOffset: "2px",
						},
					}}
				>
					{switchElement}
				</Box>
			</Tooltip>
		);
	}

	if (labelTextType === "none") return switchElement;

	return (
		<div className={styles.switchRow}>
			<Typography
				{...slotProps?.switchLabel}
				id={leftLabelId}
				sx={{
					color: disabled ? "#767676" : "#000000",
					...slotProps?.switchLabel?.sx,
				}}
				className={`${styles.switchLabel} ${slotProps?.switchLabel?.className || ""}`}
			>
				{checked && labelTextType !== "dual" ? labelOnText : labelOffText}
			</Typography>
			{switchElement}
			{labelTextType === "dual" && (
				<Typography
					{...slotProps?.switchLabel}
					id={rightLabelId}
					sx={{
						color: disabled ? "#767676" : "#000000",
						...slotProps?.switchLabel?.sx,
					}}
					className={`${styles.switchLabel} ${slotProps?.switchLabel?.className || ""}`}
				>
					{labelOnText}
				</Typography>
			)}
		</div>
	);
}

export function CoreSwitchSkeleton({
	width = "50px",
	height = "27px",
	labelTextType = "none",
}: Pick<CoreSwitchProps, "width" | "height" | "labelTextType">) {
	const switchSkeleton = <Skeleton variant="rounded" width={width} height={height} sx={{ borderRadius: 25 }} />;

	if (labelTextType === "none") {
		return switchSkeleton;
	}

	return (
		<div style={{ display: "flex", alignItems: "center" }}>
			<Skeleton
				variant="text"
				width={50}
				sx={{
					marginRight: "7px",
					marginTop: "1px",
					alignSelf: "center",
				}}
			/>
			{switchSkeleton}
		</div>
	);
}
